"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../hooks/api/useApi";
import { useDispatch } from "react-redux";
import {
  savePermissions,
  saveProfile,
} from "../../../features/merchant/merchantsSlice";
import { AppDispatch } from "../../../store";
import { formatTime } from "../../../utils/utils";

const Otp: React.FC<{ path: string }> = ({ path }) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(10 * 60);
  const navigate = useNavigate();
  const api = useApi();

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const accessKey = localStorage.getItem("accessKey");
    if (!accessKey) {
      !path.includes("admin") ? navigate("/login") : navigate("/admin/login");
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          localStorage.removeItem("accessKey");
          !path.includes("admin")
            ? navigate("/login")
            : navigate("/admin/login");
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const accessKey = localStorage.getItem("accessKey");
    if (!accessKey) {
      !path.includes("admin") ? navigate("/login") : navigate("/admin/login");
      return;
    }

    const result = await api.post("access/login", {
      accessKey: accessKey,
      accessOtp: otp.trim(),
    });

    if (result.statusCode === "00") {
      const merchant = {
        firstName: result?.data?.profile?.firstName,
        lastName: result?.data?.profile?.lastName,
        username: result?.data.profile?.username,
        merchantId: result?.data?.roles[0]?.merchantId,
        merchantName: result?.data?.roles[0]?.merchantName,
        roleAccessReference: result?.data?.profile?.roleAccessReference,
      };

      const role = result?.data?.roles[0]?.roleName;

      localStorage.setItem(
        "active",
        result?.data?.profile?.activeMerchantId ||
          result?.data?.roles[0]?.merchantId
      );
      localStorage.setItem("role", role);

      dispatch(saveProfile(merchant));
      dispatch(
        savePermissions({ permissions: result?.data?.roles[0]?.permissions })
      );

      localStorage.setItem("token", result?.data?.token);

      !path.includes("admin")
        ? navigate("/dashboard")
        : navigate("/admin/dashboard");
    } else {
      setError("Invalid or Expired OTP");
    }
  };

  return (
    <section className="flex w-full items-center justify-center  md:p-8 lg:p-16">
      <motion.div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg sm:p-8"
        initial={{ x: 100, opacity: 0, scale: 0.8 }}
        animate={{ x: 0, opacity: 1, scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 15,
          delay: 0.1,
        }}
      >
        <div className="mb-6">
          <h1 className="text-xl font-medium text-gray-900">
            Please enter the OTP sent to your email address or phone number
          </h1>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
            {error}
          </div>
        )}

        {api.error && !error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
            {api.error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <input
              id="otp"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-3 text-gray-900 placeholder-gray-400 focus:outline-none"
              placeholder="One time password"
            />
          </div>

          <motion.button
            type="submit"
            disabled={api.isLoading || otp.length === 0}
            className="w-full rounded-md bg-red-600 py-3 text-center font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {api.isLoading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </span>
            ) : (
              "Continue"
            )}
          </motion.button>

          <div className="text-center text-sm text-gray-500">
            OTP expires in{" "}
            <span className="font-medium text-red-">
              {formatTime(timeLeft)}
            </span>
          </div>
        </form>
      </motion.div>
    </section>
  );
};

export default Otp;
